import { Box, Icon, Tooltip, Text, Heading, Button, IconButton } from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { LogoTextDark, LogoLight, IconStonks } from 'assets/icons';
import { PLANS } from 'global/plans';
import { roles } from 'global/rolesAndPermissions';
import { routePaths } from 'global/routePaths';
import { useAllowedSidebarData } from 'hooks/common/useAllowedSidebarData';
import { useCompanyInfo, useUserInfo } from 'store/useUserStore';

import SidebarItem from './SidebarItem';

export interface ISidebarProps {
  isNavOpen: boolean;
  onNavClose?: () => void;
}

const Sidebar: React.FC<ISidebarProps> = ({ isNavOpen, onNavClose }) => {
  const { t } = useTranslation(['common']);

  const { allowedSideBarData } = useAllowedSidebarData();
  const companyInfo = useCompanyInfo();
  const userInfo = useUserInfo();

  const navigate = useNavigate();

  return (
    <>
      <Box px="4" py="3" display="flex" alignItems="center">
        {isNavOpen ? (
          <Icon as={LogoTextDark} height="85%" width="auto" />
        ) : (
          <Icon as={LogoLight} height="48px" width="48px" />
        )}
      </Box>
      <Box
        as="nav"
        mt="5"
        color="white"
        height={{ base: 'calc(100% - 72px)', md: '100%' }}
        pb="10"
        display="flex"
        flexDir="column"
        justifyContent={isNavOpen ? 'space-between' : 'initial'}
      >
        <Box as="ul" px="2" listStyleType="none">
          {allowedSideBarData?.map(({ label, ...sidebarRestData }: any) => {
            return (
              <Box key={label} as="li">
                <SidebarItem {...sidebarRestData} label={label} onNavClose={onNavClose} isNavOpen={isNavOpen} />
              </Box>
            );
          })}
        </Box>

        {userInfo?.roleId === roles.ADMIN &&
          companyInfo?.plan?.toLowerCase() !== PLANS.freeTrial &&
          !(companyInfo?.plan?.toLowerCase() === PLANS.premium) &&
          (isNavOpen ? (
            <Box bg="rgba(255, 243, 230, 0.1)" px="8px" py="10px" m="16px" borderRadius="base">
              <Icon as={IconStonks} h="12px" w="20px" mb="2.5" />
              <Heading as="h5" fontSize="14px" fontWeight="bold" lineHeight="122%" mb="2">
                {companyInfo?.plan?.toLowerCase() === PLANS.basic && t('common:premiumUpsaleHeading')}
                {companyInfo?.plan?.toLowerCase() === PLANS.pro && t('common:proUpsaleHeading')}
              </Heading>
              <Text fontWeight="300" fontSize="13px" mb="5">
                {companyInfo?.plan?.toLowerCase() === PLANS.basic && t('common:premiumUpsaleBody')}
                {companyInfo?.plan?.toLowerCase() === PLANS.pro && t('common:proUpsaleBody')}
              </Text>
              <Button colorScheme="secondary" w="full" onClick={() => navigate(routePaths.accountBilling.upgrade)}>
                {(companyInfo?.plan?.toLowerCase() === PLANS.basic && t('common:getProNow')) ||
                  (companyInfo?.plan?.toLowerCase() === PLANS.pro && t('common:getPremiumNow'))}
              </Button>
            </Box>
          ) : (
            <Tooltip
              hasArrow
              arrowSize={10}
              placement="right"
              bg="secondary.500"
              label={
                companyInfo?.plan?.toLowerCase() === PLANS.basic
                  ? t('common:getProNow')
                  : companyInfo?.plan?.toLowerCase() === PLANS.pro
                    ? t('common:getPremiumNow')
                    : ''
              }
              isDisabled={isNavOpen}
              py="2"
              px="3"
              mt="12"
              borderRadius="lg"
            >
              <Box>
                <IconButton
                  aria-label={
                    companyInfo?.plan?.toLowerCase() === PLANS.basic
                      ? t('common:getProNow')
                      : companyInfo?.plan?.toLowerCase() === PLANS.pro
                        ? t('common:getPremiumNow')
                        : ''
                  }
                  variant="unstyled"
                  cursor="pointer"
                  mt="12"
                  mx="16px"
                  onClick={() => navigate(routePaths.accountBilling.upgrade)}
                >
                  <IconStonks />
                </IconButton>
              </Box>
            </Tooltip>
          ))}
      </Box>
    </>
  );
};

export default Sidebar;
